import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { newId, useAppStore } from '@restorant/core';
import type { ExpenseCategory } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import { useExpenseCategories } from '../menu';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { Sheet, SheetButton, Toggle } from './Sheet';
import { T } from './Text';
import { TextField } from './TextField';

/**
 * The icons offered for a spending head. A short, deliberate list rather than
 * every icon in the set: these are the shapes of what a restaurant pays for,
 * and a grid of sixty would take longer to choose from than to type a name.
 */
const HEAD_ICONS: IconName[] = [
  'wallet', 'salary', 'rent', 'material', 'vegetables', 'utilities',
  'maintenance', 'note', 'store', 'parcel', 'clock', 'percent',
];

function IconPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const colors = useColors();
  const t = useT();
  return (
    <View style={{ gap: 8 }}>
      <T size={11.5} weight={600} color={colors.textFaint}>
        {t('icon')}
      </T>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {HEAD_ICONS.map((name) => {
          const active = name === value;
          return (
            <Pressable
              key={name}
              onPress={() => onChange(name)}
              style={{
                width: 44,
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: radius.md,
                borderWidth: 1,
                borderColor: active ? colors.gold : colors.line,
                backgroundColor: active ? colors.surface2 : 'transparent',
              }}
            >
              <Icon name={name} size={20} color={active ? colors.gold : colors.textDim} />
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

/**
 * Add, rename or retire one spending head.
 *
 * Deleting a head does NOT delete what was spent under it — the entries drop
 * their head and read as uncategorised, which is what the ON DELETE SET NULL
 * on the column does on the server. The confirm label says so, because the
 * alternative reading ("this erases two years of rent") is the one that would
 * stop an owner tidying up their list.
 */
export function ExpenseHeadEditor({
  head,
  visible,
  onClose,
}: {
  /** null = creating a new one. */
  head: ExpenseCategory | null;
  visible: boolean;
  onClose: () => void;
}) {
  const colors = useColors();
  const t = useT();
  const all = useExpenseCategories(true);
  const restaurantId = useAppStore((s) => s.restaurantId);
  const upsert = useAppStore((s) => s.upsertExpenseCategory);
  const remove = useAppStore((s) => s.deleteExpenseCategory);
  const showToast = useAppStore((s) => s.showToast);

  const [name, setName] = useState('');
  const [nameHi, setNameHi] = useState('');
  const [nameGu, setNameGu] = useState('');
  const [icon, setIcon] = useState('wallet');
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setName(head?.name ?? '');
    setNameHi(head?.nameHi ?? '');
    setNameGu(head?.nameGu ?? '');
    setIcon(head?.icon ?? 'wallet');
    setActive(head?.active ?? true);
    setError(null);
    setConfirmDelete(false);
  }, [head, visible]);

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return setError(t('head_name_required'));
    // The database has a unique index on (restaurant_id, lower(name)), so a
    // duplicate would be refused on upload and sit in the outbox retrying
    // forever. Catching it here is what turns that into a message.
    const clash = all.some(
      (c) => c.id !== head?.id && c.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (clash) return setError(t('head_name_taken'));

    upsert({
      // Ids are global primary keys, so a second shop must not reuse 'rent'.
      // The prefix is the same rule the menu and the migration both follow.
      id: head?.id ?? `${(restaurantId ?? '').slice(0, 8)}-exp-${newId().slice(0, 8)}`,
      name: trimmed,
      nameHi: nameHi.trim(),
      nameGu: nameGu.trim(),
      icon,
      sortOrder: head?.sortOrder ?? Math.max(0, ...all.map((c) => c.sortOrder)) + 10,
      active,
    });
    showToast(t('saved_toast'));
    onClose();
  };

  const destroy = () => {
    if (!head) return;
    if (!confirmDelete) return setConfirmDelete(true);
    remove(head.id);
    showToast(t('deleted_toast'));
    onClose();
  };

  return (
    <Sheet
      visible={visible}
      title={head ? t('edit_head') : t('add_head')}
      onClose={onClose}
      footer={
        <>
          {head ? (
            <SheetButton
              label={confirmDelete ? t('delete_head_confirm') : t('delete')}
              icon="trash"
              tone="danger"
              onPress={destroy}
            />
          ) : null}
          <SheetButton label={t('save')} icon="check" tone="primary" onPress={save} />
        </>
      }
    >
      <TextField
        testID="head-name"
        label={t('head_name')}
        value={name}
        onChangeText={setName}
        placeholder={t('exp_other')}
      />

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <TextField
          testID="head-name-hi"
          label={t('name_hi')}
          value={nameHi}
          onChangeText={setNameHi}
        />
        <TextField
          testID="head-name-gu"
          label={t('name_gu')}
          value={nameGu}
          onChangeText={setNameGu}
        />
      </View>

      <IconPicker value={icon} onChange={setIcon} />

      <Toggle label={t('active_label')} value={active} onChange={setActive} />

      {error ? (
        <T size={12} color={colors.spice}>
          {error}
        </T>
      ) : null}
    </Sheet>
  );
}
